import React from 'react';
import { motion } from "framer-motion";
import { RotateCcw } from "lucide-react";

// Telegram Icon Component
export const TelegramIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.665 3.717l-17.73 6.837c-1.21.486-1.203 1.161-.222 1.462l4.552 1.42 10.532-6.645c.498-.303.953-.14.579.192l-8.533 7.701h-.002l.002.001-.314 4.692c.46 0 .663-.211.921-.46l2.211-2.15 4.599 3.397c.848.467 1.457.227 1.668-.787l3.019-14.228c.309-1.239-.473-1.8-1.282-1.432z" />
    </svg>
);

// Telegram Channel Configuration
const TELEGRAM_CONFIG = {
    channelUrl: 'https://t.me/sdet_video_updates',
    channelName: 'SDET Video Updates',
    qrCodeUrl: 'https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://t.me/sdet_video_updates&color=0088cc',
    features: ['No Spam', 'Weekly Updates', 'Top Creators'],
    botLabel: 'AI-Powered Bot',
    description: 'Missed a testing update? Get concise, AI-powered summaries of the latest videos from top SDET creators directly to your Telegram.'
};

/**
 * TelegramQRCard Component
 * 
 * Displays a full-width card with Telegram QR code and channel information.
 * Used as the back side of the flip card in the Contact section.
 * 
 * @param {Object} props
 * @param {boolean} props.isVisible - Whether the card is currently visible (flipped to back)
 * @param {Function} props.onFlipBack - Callback function to flip back to the front side
 */
const TelegramQRCard = ({ onFlipBack }) => {
    return (
        <div className="w-full h-full bg-gradient-to-br from-[#0088cc] via-[#0099dd] to-[#00b4e6] rounded-3xl p-1 shadow-2xl shadow-[#0088cc]/20">
            <div className="w-full h-full bg-white dark:bg-gray-900 rounded-[22px] p-4 sm:p-5 relative overflow-hidden flex flex-col items-center justify-between">
                    {/* Decorative elements */}
                    <div className="absolute top-4 right-4 w-32 h-32 bg-gradient-to-br from-[#0088cc]/10 to-[#00b4e6]/10 rounded-full blur-3xl pointer-events-none"></div>
                    <div className="absolute bottom-4 left-4 w-24 h-24 bg-gradient-to-br from-[#0088cc]/10 to-[#00b4e6]/10 rounded-full blur-3xl pointer-events-none"></div>

                    <div className="relative z-10 flex flex-col items-center justify-center gap-4 py-1 w-full">
                        {/* QR Code Section */}
                        <QRCodePhoneFrame />

                        {/* Text Content */}
                        <TelegramChannelInfo />
                    </div>

                    {/* Flip Back Button */}
                    <FlipBackButton onClick={onFlipBack} />
                </div>
            </div>
    );
};

/**
 * QR Code displayed inside a phone frame mockup
 */
const QRCodePhoneFrame = () => {
    return (
        <div className="flex-shrink-0 relative">
            {/* Phone frame */}
            <div className="relative bg-gradient-to-b from-gray-800 to-gray-900 rounded-[2rem] p-2 shadow-xl">
                {/* Phone notch */}
                <div className="absolute top-4 left-1/2 -translate-x-1/2 w-16 h-1.5 bg-gray-700 rounded-full"></div>

                {/* Phone screen */}
                <div className="bg-gradient-to-br from-[#0088cc] to-[#0099dd] rounded-[1.5rem] p-4 pt-6">
                    {/* Telegram header inside phone */}
                    <div className="flex items-center justify-center gap-2 mb-3">
                        <motion.div
                            animate={{ x: [0, 3, 0], y: [0, -2, 0] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        >
                            <TelegramIcon className="w-5 h-5 text-white" />
                        </motion.div>
                        <span className="text-white text-xs font-semibold">SDET Updates</span>
                    </div>

                    {/* QR Code Container */}
                    <div className="bg-white rounded-xl p-2 sm:p-3 shadow-inner mt-1 sm:mt-0">
                        <img
                            src={TELEGRAM_CONFIG.qrCodeUrl}
                            alt="Scan to join Telegram Channel"
                            className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32"
                        />
                    </div>

                    {/* Scan badge */}
                    <motion.div
                        className="mt-3 flex items-center justify-center gap-1.5"
                        animate={{ scale: [1, 1.05, 1] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                    >
                        <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                        <span className="text-white text-[10px] font-medium uppercase tracking-wider">Scan to Join</span>
                        <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                    </motion.div>
                </div>
            </div>

            {/* Floating paper plane decoration */}
            <motion.div
                className="absolute -top-3 -right-3 bg-gradient-to-br from-[#0088cc] to-[#00b4e6] p-2.5 rounded-full shadow-lg"
                animate={{
                    y: [0, -5, 0],
                    rotate: [0, 10, 0]
                }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
                <TelegramIcon className="w-4 h-4 text-white" />
            </motion.div>
        </div>
    );
};

/**
 * Telegram channel information and CTA section
 */
const TelegramChannelInfo = () => {
    return (
        <div className="text-center flex-1 w-full flex flex-col items-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#0088cc]/10 to-[#00b4e6]/10 px-4 py-1.5 rounded-full mb-4">
                <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0088cc] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#0088cc]"></span>
                </span>
                <span className="text-[#0088cc] dark:text-[#00b4e6] text-xs font-bold uppercase tracking-wider">
                    {TELEGRAM_CONFIG.botLabel}
                </span>
            </div>

            <h3 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-white mb-2">
                {TELEGRAM_CONFIG.channelName}
            </h3>

            <p className="text-gray-600 dark:text-gray-300 text-xs mb-4 leading-relaxed">
                Missed a testing update? Get concise, <span className="text-[#0088cc] dark:text-[#00b4e6] font-semibold">AI-powered summaries</span> of the latest videos from top SDET creators directly to your Telegram.
            </p>

            {/* Features list */}
            <div className="flex flex-wrap justify-center gap-1.5 mb-5">
                {TELEGRAM_CONFIG.features.map((feature, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-3 py-1.5 rounded-lg">
                        <svg className="w-3.5 h-3.5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature}
                    </span>
                ))}
            </div>

            {/* CTA Button */}
            <motion.a
                href={TELEGRAM_CONFIG.channelUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2.5 bg-gradient-to-r from-[#0088cc] to-[#0099dd] hover:from-[#0077bb] hover:to-[#0088cc] text-white px-7 py-3 rounded-2xl font-semibold transition-all duration-300 shadow-md shadow-[#0088cc]/30"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
            >
                <TelegramIcon className="w-5 h-5" />
                Join on Telegram
                <svg className="w-4 h-4 -mr-1" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
            </motion.a>
        </div>
    );
};

/**
 * Button to flip back to the contact information
 */
const FlipBackButton = ({ onClick }) => {
    return (
        <motion.button
            onClick={onClick}
            className="w-full z-20 flex items-center justify-center gap-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 px-5 py-3 rounded-2xl text-sm font-semibold text-gray-700 dark:text-gray-300 transition-all duration-300 shadow-sm mx-auto"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.95 }}
        >
            <RotateCcw className="w-4 h-4" />
            Back to Contact
        </motion.button>
    );
};

export default TelegramQRCard;
